<script lang="ts" setup>
/**
 * @name Dashboard.vue
 * @version 1.0.0
 * @description Vista para mostrar datos de bitácora de instituciones
 * @author
 * @changelog
 * 1.0.0 - 2024/01/01 - @usuario - Versión inicial
 */

import { computed, onMounted, ref, watch } from 'vue'
import { BitacoraData, IResponsable, TransactionData } from '@shared/types/base'
import { auth, dbRegistros, database } from '@/firebase/config'
import { get, ref as dbRef } from 'firebase/database'
import { doc, getDoc, setDoc, Timestamp, deleteField, collection, query, where, getDocs } from 'firebase/firestore'
import BaseModal from '@/components/BaseModal.vue'
import { EPlan, PLAN_OPTIONS } from '@shared/types/enums'
import { apiService } from '@/services/apiService'
import ResponsablesModal from './ResponsablesModal.vue'
import { getInstitutionCounter, existsInstitution, convertFirestoreTimestamp } from '@/components/utils/functions'
import { VueDatePicker } from '@vuepic/vue-datepicker'
import { es } from 'date-fns/locale/es'
import { useStore } from 'vuex'
/**
 * @description Props del componente
 */
const error = ref<string | null>(null)
const submitting = ref(false)
const successMessage = ref<string | null>(null)
const showErrorModal = ref(false)
const showSuccessModal = ref(false)
const currentInstitutionId = ref<number>(0)
const store = useStore()
const currentInstitution = computed(() => store.getters['global/currentInstitution'])

const tabs = [
	{ icon: 'school', title: 'Registro de la institución', content: 'Registro de la institución' },
	{ icon: 'school', title: 'Registro de bitácora', content: 'Registro de bitácora' }
]
const value = ref(tabs[0].title)
const currentTab = computed(() => tabs.find(({ title }) => title === value.value))
const configuracionValue = ref(false)
const showModalUploadCertificate = ref(false)
const editarActual = ref(false)
/**
 * @description Referencia para los datos de bitácora
 */
const bitacora = ref<BitacoraData | null>(null)

/**
 * @description Formulario de datos de la institución
 */
const formData = ref({
	idInstitution: '',
	institutionName: '',
	idNombreInstitucion: 0,
	totalDocumentsProduction: 0,
	totalDocumentsTest: 0,
	modificationDate: Date.now(),
	LastUserWhomodified: '',
	pricePerDocument: 0,
	plan: EPlan.POSTPAID as EPlan,
	fechaInicioContrato: Date.now(),
	fechaFinContrato: new Date(Date.now()).setDate(new Date(Date.now()).getDate() + 1),
	precioPlan: 0,
	limitDocumentsPostpaid: 20,
	iva: 0,
	config: {
		certificates: {
			decimals: 0,
			cycleMask: String.raw`\d{4}-[\dAB]{1,2}`,
			hasAccents: false,
			mayus: false
		},
		degree: {
			conectorCarreraNivel: '',
			hasAccents: false,
			services: {
				id: '',
				url: '',
				user: '',
				pass: ''
			}
		}
	},
	testMode: {
		config: {
			degree: {
				services: {
					id: '',
					url: '',
					user: '',
					pass: ''
				}
			}
		}
	},
	Responsable: {} as { [key: string]: IResponsable }
})

const responsablesRows = computed(
	() =>
		Object.entries(formData.value.Responsable)
			.map(([curp, data]) => {
				if (!curp || !data) return null

				const nombreCompleto = [data.nombre, data.primerApellido, data.segundoApellido].filter(Boolean).join(' ')

				return {
					curp,
					nombreCompleto,
					phone: data.phone ?? '',
					cargoCertificados: data.certificates?.cargo ?? '',
					cargoTitulos: data.degrees?.cargo ?? '',
					abrTitulo: data.degrees?.abrTitulo ?? ''
				}
			})
			.filter(Boolean) as {
			curp: string
			nombreCompleto: string
			phone: string
			cargoCertificados: string
			cargoTitulos: string
			abrTitulo: string
		}[]
)

const minDateFinContrato = computed(() => {
	const inicio = formData.value.fechaInicioContrato
	if (!inicio) return undefined
	const d = new Date(inicio)
	d.setDate(d.getDate() + 1)
	return d
})

const mesSeleccionado = ref({ month: new Date().getMonth(), year: new Date().getFullYear() })
watch(
	() => formData.value.precioPlan,
	val => {
		if (typeof val === 'number' && val < 0) formData.value.precioPlan = 0
	}
)

onMounted(async () => {
	currentInstitutionId.value = await getInstitutionCounter()
	formData.value.idInstitution = String(Number(currentInstitutionId.value) + 1)
})

/**
 * @description Valida el formulario antes de enviar
 */
const validarFormulario = (): boolean => {
	if (!formData.value.idInstitution.trim()) {
		error.value = 'El ID de la institución es requerido'
		return false
	}
	if (!formData.value.institutionName.trim()) {
		error.value = 'El nombre de la institución es requerido'
		return false
	}
	if (formData.value.idNombreInstitucion <= 0) {
		error.value = 'El ID de nombre de la institución es requerido'
		return false
	}
	return true
}
const validarBitacora = (): boolean => {
	if (!formData.value.idInstitution.trim()) {
		error.value = 'El ID de la institución es requerido'
		return false
	}
	if (!formData.value.institutionName.trim()) {
		error.value = 'El nombre de la institución es requerido'
		return false
	}
	if (formData.value.plan !== EPlan.UNLIMITED && formData.value.plan !== EPlan.BASIC) {
		if (formData.value.pricePerDocument <= 0) {
			error.value = 'El precio por documento no puede ser cero o negativo'
			return false
		}
	}
	if (formData.value.plan === EPlan.PREPAID) {
		if (formData.value.totalDocumentsProduction <= 0) {
			error.value = 'El total de documentos de producción no puede ser cero o negativo'
			return false
		}
		if (formData.value.totalDocumentsTest <= 0) {
			error.value = 'El total de documentos de prueba no puede ser cero o negativo'
			return false
		}
	}
	return true
}
/**
 * @description Obtiene los datos de bitácora desde Firestore
 *
 * Con Custom Claims, las reglas de Firestore verifican automáticamente
 * si el usuario tiene permisos de administrador. Si el usuario no es administrador,
 * Firestore rechazará la petición con permission-denied.
 */
/**
 * @returns `true` si la bitácora aún no existe (se puede crear); `false` si ya existe o hay error.
 */
const obtenerBitacora = async (idInstitution: string): Promise<boolean> => {
	try {
		error.value = null

		if (!idInstitution) {
			console.warn('No se proporcionó ID de institución')
			return false
		}

		if (!auth.currentUser) {
			error.value = 'Error de autenticación. Por favor, inicia sesión nuevamente'
			return false
		}

		const bitacoraSnap = await getDoc(doc(dbRegistros, 'bitacora', idInstitution))
		return !bitacoraSnap.exists()
	} catch (err: any) {
		console.error('Error al obtener bitácora:', err)
		return false
	}
}

/**
 * @description Maneja el envío del formulario
 */
const handleSubmit = async () => {
	error.value = null
	successMessage.value = null

	if (!validarFormulario()) {
		showErrorModal.value = true
		return
	}
	if (Number(formData.value.idInstitution) > Number(currentInstitutionId.value + 1)) {
		error.value = 'El ID de la institución no puede ser mayor al contador de instituciones'
		showErrorModal.value = true
		return
	}

	submitting.value = true
	try {
		// Actualizar la fecha de modificación
		formData.value.modificationDate = Date.now()

		if (currentInstitutionId.value.toString() !== formData.value.idInstitution.toString() && !(await existsInstitution(formData.value.idInstitution.toString()))) {
			// Convertir el ID a número antes de enviarlo
			const institutionIdNumber = Number(formData.value.idInstitution)

			if (Number.isNaN(institutionIdNumber)) {
				throw new TypeError('El ID de institución debe ser un número válido')
			}
			await apiService.post('registerNewInstitution', {
				nombreInstitucion: formData.value.institutionName,
				institutionId: institutionIdNumber, // Enviar como número, no como string
				idNombreInstitucion: formData.value.idNombreInstitucion,
				config: formData.value.config,
				testMode: formData.value.testMode,
				Responsable: formData.value.Responsable
			})

			// Recargar el contador después de registrar exitosamente
			currentInstitutionId.value = await getInstitutionCounter()
		}

		successMessage.value = 'Institución registrada correctamente'
		showSuccessModal.value = true
		cleanForm()
		configuracionValue.value = false
	} catch (err: any) {
		error.value = err.message || 'Error al guardar los datos'
		showErrorModal.value = true
		cleanForm()
	} finally {
		submitting.value = false
	}
}
const handleSubmitBitacora = async () => {
	error.value = null
	successMessage.value = null

	if (!validarBitacora()) {
		showErrorModal.value = true
		return
	}

	submitting.value = true
	try {
		if (!(await existsInstitution(formData.value.idInstitution.toString()))) {
			error.value = 'La institución no está registrada'
			showErrorModal.value = true
			return
		}

		formData.value.modificationDate = Date.now()

		if (auth.currentUser) {
			const curpSnapshot = await get(dbRef(database, `users/${auth.currentUser.uid}/curp`))
			formData.value.LastUserWhomodified = curpSnapshot.val() || 'N/A'
		}

		// Cortocircuito al editar: no hace falta consultar si la bitácora ya existe
		if (!editarActual.value && !(await obtenerBitacora(formData.value.idInstitution))) {
			error.value = 'La institución ya está registrada'
			showErrorModal.value = true
			cleanForm()
			return
		}

		const payload: Record<string, any> = {
			institutionName: formData.value.institutionName,
			totalDocumentsProduction: formData.value.totalDocumentsProduction,
			totalDocumentsTest: formData.value.totalDocumentsTest,
			modificationDate: Timestamp.fromDate(new Date()),
			LastUserWhomodified: formData.value.LastUserWhomodified || '',
			pricePerDocument: formData.value.pricePerDocument,
			fechaInicioContrato: formData.value.fechaInicioContrato,
			fechaFinContrato: formData.value.fechaFinContrato,
			precioPlan: formData.value.precioPlan,
			iva: formData.value.iva,
			plan: formData.value.plan
		}

		if (formData.value.plan === EPlan.POSTPAID) {
			payload.limitDocumentsPostpaid = formData.value.limitDocumentsPostpaid
		} else {
			payload.limitDocumentsPostpaid = deleteField()
		}

		await setDoc(doc(dbRegistros, 'bitacora', formData.value.idInstitution), payload, { merge: true })
		bitacora.value = { ...formData.value }

		successMessage.value = 'Bitácora registrada correctamente'
		showSuccessModal.value = true
		cleanForm()
	} catch (err: any) {
		error.value = err.message || 'Error al guardar los datos'
		showErrorModal.value = true
		cleanForm()
	} finally {
		submitting.value = false
	}
}
watch(currentInstitution, (newVal, oldVal) => {
	if (newVal !== oldVal) {
		cleanForm()
	}
})
watch(editarActual, async newVal => {
	if (newVal) {
		const bitacoraRef = doc(dbRegistros, 'bitacora', currentInstitution.value)
		const bitacoraSnap = await getDoc(bitacoraRef)
		if (bitacoraSnap.exists()) {
			const bitacora = bitacoraSnap.data() as BitacoraData
			formData.value.idInstitution = currentInstitution.value
			formData.value.institutionName = bitacora.institutionName || ''
			formData.value.totalDocumentsProduction = bitacora.totalDocumentsProduction || 0
			formData.value.totalDocumentsTest = bitacora.totalDocumentsTest || 0
			formData.value.modificationDate = bitacora.modificationDate || 0
			formData.value.pricePerDocument = bitacora.pricePerDocument || 0
			formData.value.plan = bitacora.plan || (EPlan.POSTPAID as EPlan)
			formData.value.fechaInicioContrato = bitacora.fechaInicioContrato ? convertFirestoreTimestamp(bitacora.fechaInicioContrato) : Date.now()
			formData.value.fechaFinContrato = bitacora.fechaFinContrato ? convertFirestoreTimestamp(bitacora.fechaFinContrato) : Date.now()
			formData.value.precioPlan = bitacora.precioPlan || 0
			formData.value.iva = bitacora.iva || 0
			formData.value.limitDocumentsPostpaid = bitacora.limitDocumentsPostpaid || 0
		} else {
			error.value = 'No se encontró bitácora de la institución actual, por favor cree una nueva bitácora'
			showErrorModal.value = true
			editarActual.value = false
			cleanForm()
		}
	} else {
		cleanForm()
	}
})

const cleanForm = () => {
	formData.value = {
		idInstitution: '',
		institutionName: '',
		idNombreInstitucion: 0,
		totalDocumentsProduction: 0,
		totalDocumentsTest: 0,
		modificationDate: Date.now(),
		LastUserWhomodified: '',
		pricePerDocument: 0,
		plan: EPlan.POSTPAID as EPlan,
		fechaInicioContrato: Date.now(),
		fechaFinContrato: Date.now(),
		precioPlan: 0,
		iva: 0,
		limitDocumentsPostpaid: 0,
		config: {
			certificates: {
				decimals: 0,
				cycleMask: String.raw`\d{4}-[\dAB]{1,2}`,
				hasAccents: false,
				mayus: false
			},
			degree: {
				conectorCarreraNivel: '',
				hasAccents: false,
				services: {
					id: '',
					url: '',
					user: '',
					pass: ''
				}
			}
		},
		testMode: {
			config: {
				degree: {
					services: {
						id: '',
						url: '',
						user: '',
						pass: ''
					}
				}
			}
		},
		Responsable: {} as { [key: string]: IResponsable }
	}
	// Actualizar el ID sugerido después de limpiar el formulario
	formData.value.idInstitution = String(Number(currentInstitutionId.value) + 1)
	editarActual.value = false
}
/**
 * @description Resetea el formulario a los valores por defecto
 */
const handleReset = () => {
	cleanForm()
	error.value = null
	successMessage.value = null
}
const uploadCertificate = () => {
	showModalUploadCertificate.value = true
}
const eliminarResponsable = (curp: string) => {
	delete formData.value.Responsable[curp]
}
const onUpdateResponsable = (value: { [key: string]: IResponsable }) => {
	formData.value.Responsable = value
}
const calcularIVA = () => {
	formData.value.iva = formData.value.precioPlan * 0.16
}
watch(() => formData.value.precioPlan, calcularIVA, { immediate: true })
const calcularPrecioPlan = async (mesSeleccionado: { month: number; year: number }) => {
	const transaccionesRef = collection(dbRegistros, 'transacciones')
	mesSeleccionado.month = mesSeleccionado.month + 1
	const inicioMes = new Date(mesSeleccionado.year, mesSeleccionado.month - 1, 1, 0, 0, 0, 0)
	const finMes = new Date(mesSeleccionado.year, mesSeleccionado.month, 0, 23, 59, 59, 999)

	const inicioTimestamp = Timestamp.fromDate(inicioMes)
	const finTimestamp = Timestamp.fromDate(finMes)

	const q = query(transaccionesRef, where('idInstitution', '==', currentInstitution.value), where('dateModification', '>=', inicioTimestamp), where('dateModification', '<=', finTimestamp))
	const transaccionesSnapshot = await getDocs(q)
	let totalGastado = 0
	for (const doc of transaccionesSnapshot.docs) {
		const trans = doc.data() as TransactionData
		if (trans.typeTransaction === 'decremento') {
			totalGastado += (trans.quantityDocuments || 0) * (trans.pricePerDocument || bitacora.value?.pricePerDocument || 0)
		}
	}
	formData.value.precioPlan = totalGastado
}
</script>

<template>
	<va-card class="bitacora-card">
		<va-card-title class="header-gradient">
			<div class="header-content">
				<div class="header-main">
					<div class="header-icon-wrapper">
						<va-icon name="account_balance" class="header-icon" />
					</div>
					<div class="header-text">
						<h2 class="header-title">Registro de una institución</h2>
					</div>
				</div>
			</div>
		</va-card-title>

		<va-card-content class="card-content">
			<VaTabs v-model="value" grow>
				<template #tabs>
					<VaTab v-for="tab in tabs" :key="tab.title" :name="tab.title">
						<VaIcon :name="tab.icon" size="small" class="mr-2" />
						{{ tab.title }}
					</VaTab>
				</template>
				<VaCard class="full-width-card" style="margin-top: 1rem">
					<VaCardContent v-if="currentTab?.title === 'Registro de la institución'">
						<div class="section-title">
							<div class="section-title-content">
								<va-icon name="description" class="title-icon" />
								<span>Datos de la institución a registrar</span>
							</div>
						</div>
						<form class="form-container">
							<div class="form-grid">
								<!-- ID de Institución -->

								<div class="package-grid">
									<div class="package-card">
										<div class="card-gradient transaction-gradient"></div>
										<div class="card-content-wrapper">
											<div class="card-header">
												<div class="package-avatar transaction-avatar">
													<va-icon name="account_balance" class="label-icon" />
												</div>
												<div class="card-title-group">
													<h3 class="card-title">Id de la base de datos</h3>
												</div>
											</div>
											<div class="card-value">
												<va-input
													v-model="formData.idInstitution"
													placeholder="Ingrese el Id de la base de datos"
													class="form-input"
													:rules="[value => !!value || 'El Id de la base de datos es requerido']" />
											</div>
										</div>
									</div>
								</div>
								<!-- Nombre de Institución -->
								<div class="package-grid">
									<div class="package-card">
										<div class="card-gradient transaction-gradient"></div>
										<div class="card-content-wrapper">
											<div class="card-header">
												<div class="package-avatar transaction-avatar">
													<va-icon name="school" class="label-icon" />
												</div>
												<div class="card-title-group">
													<h3 class="card-title">Nombre de Institución</h3>
												</div>
											</div>
											<div class="card-value">
												<va-input
													v-model="formData.institutionName"
													placeholder="Ingrese el nombre de la institución"
													class="form-input"
													:rules="[value => !!value || 'El nombre de institución es requerido']" />
											</div>
										</div>
									</div>
								</div>
								<div class="package-grid">
									<div class="package-card">
										<div class="card-gradient transaction-gradient"></div>
										<div class="card-content-wrapper">
											<div class="card-header">
												<div class="package-avatar transaction-avatar">
													<va-icon name="pin" class="label-icon" />
												</div>
												<div class="card-title-group">
													<h3 class="card-title">Id de la institución</h3>
												</div>
											</div>
											<div class="card-value">
												<va-input
													v-model="formData.idNombreInstitucion"
													placeholder="Ingrese el Id de la institución"
													class="form-input"
													:rules="[value => !!value || 'El Id de la institución es requerido']" />
											</div>
										</div>
									</div>
								</div>
							</div>
							<div class="section-title">
								<div class="section-title-content">
									<va-icon name="description" class="title-icon" />
									<span>Agregar responsable de firma</span>
								</div>
							</div>
							<div class="responsables-layout">
								<div class="upload-button-container">
									<VaButton color="primary" size="small" class="upload-button" @click="uploadCertificate">
										<VaIcon name="add" class="me-2" />
										Agregar responsable
									</VaButton>
								</div>

								<div class="responsables-content">
									<div class="responsables-table" v-if="responsablesRows.length">
										<div class="va-table-responsive">
											<table class="va-table va-table--striped">
												<thead>
													<tr>
														<th>CURP</th>
														<th>Nombre</th>
														<th>Teléfono</th>
														<th>Cargo (Certificados)</th>
														<th>Cargo (Títulos)</th>
														<th>Nivel de estudios</th>
														<th>Acciones</th>
													</tr>
												</thead>
												<tbody>
													<tr v-for="row in responsablesRows" :key="row.curp">
														<td>{{ row.curp }}</td>
														<td>{{ row.nombreCompleto }}</td>
														<td>{{ row.phone }}</td>
														<td>{{ row.cargoCertificados }}</td>
														<td>{{ row.cargoTitulos }}</td>
														<td>{{ row.abrTitulo }}</td>
														<td>
															<VaButton color="danger" size="small" @click="eliminarResponsable(row.curp)">
																<VaIcon name="delete" class="me-2" />
																Eliminar
															</VaButton>
														</td>
													</tr>
												</tbody>
											</table>
										</div>
									</div>
									<div v-else class="responsables-empty text-secondary">Aún no hay responsables agregados.</div>
								</div>
							</div>
							<VaCollapse v-model="configuracionValue" class="collapse-container">
								<template #header="{ value, attrs, iconAttrs }">
									<div v-bind="attrs" class="collapse-header">
										<VaIcon name="expand_more" :class="['collapse-icon', { 'rotate-180': !value }]" v-bind="iconAttrs" />
										<div class="collapse-title">Agregar configuración de la institución</div>
									</div>
								</template>
								<template #body>
									<div class="section-title" style="margin-top: 1rem">
										<div class="section-title-content">
											<va-icon name="description" class="title-icon" />
											<span>Configuración de certificados</span>
										</div>
									</div>
									<div class="collapse-body">
										<div class="collapse-content">
											<div class="form-grid">
												<!-- CURP del responsable de firma -->

												<div class="package-grid">
													<div class="package-card">
														<div class="card-gradient transaction-gradient" style="height: 160px"></div>
														<div class="card-content-wrapper">
															<div class="card-header">
																<div class="package-avatar transaction-avatar">
																	<va-icon name="pin" class="label-icon" />
																</div>
																<div class="card-title-group">
																	<h3 class="card-title">Decimales</h3>
																	<span style="font-size: small">Este campo define los decimales que se usarán créditos de la institución</span>
																	<br />
																	<span style="font-size: small"> Ejem: 2 decimales : 9.45, 3 decimales: 9.455 ...</span>
																</div>
															</div>
															<div class="card-value" style="margin-top: 4rem">
																<va-input
																	v-model="formData.config.certificates.decimals"
																	placeholder="Ingrese el ID de la institución"
																	class="form-input"
																	:rules="[value => !!value || 'El ID de institución es requerido']" />
															</div>
														</div>
													</div>
												</div>

												<!-- Nombre de Institución -->
												<div class="package-grid">
													<div class="package-card">
														<div class="card-gradient transaction-gradient" style="height: 160px"></div>
														<div class="card-content-wrapper">
															<div class="card-header">
																<div class="package-avatar transaction-avatar">
																	<va-icon name="theater_comedy" class="label-icon" />
																</div>
																<div class="card-title-group">
																	<h3 class="card-title">Máscara de ciclo</h3>
																	<span style="font-size: smaller">Define la siguiente máscara de ciclo: 4 números, un guión y 1 o 2 caracteres números, A o B</span>
																	<br />
																	<span style="font-size: smaller"> Ejem: 0000-01, 1111-A1, 4444-1</span>
																</div>
															</div>
															<div class="card-value" style="margin-top: 2.5rem">
																<va-input
																	v-model="formData.config.certificates.cycleMask"
																	readonly
																	placeholder="Ingrese la máscara de ciclo"
																	class="form-input"
																	:rules="[value => !!value || 'La máscara de ciclo es requerida']" />
															</div>
														</div>
													</div>
												</div>
												<div class="package-grid">
													<div class="package-card">
														<div class="card-gradient transaction-gradient" style="height: 160px"></div>
														<div class="card-content-wrapper">
															<div class="card-header">
																<div class="package-avatar transaction-avatar">
																	<va-icon name="check_box" class="label-icon" />
																</div>
																<div class="card-title-group">
																	<h3 class="card-title">Quitar acentos</h3>
																	<span style="font-size: smaller">Normaliza los textos para eliminar acentos del Nombre del alumno y responsable de expedición</span>
																</div>
															</div>
															<div class="card-value" style="margin-top: 4.5rem">
																<VaRadio
																	v-model="formData.config.certificates.hasAccents"
																	:options="[
																		{ text: 'Mantener acentos', value: true },
																		{ text: 'Quitar acentos', value: false }
																	]"
																	value-by="value" />
															</div>
														</div>
													</div>
												</div>

												<div class="package-grid">
													<div class="package-card">
														<div class="card-gradient transaction-gradient" style="height: 160px"></div>
														<div class="card-content-wrapper">
															<div class="card-header">
																<div class="package-avatar transaction-avatar">
																	<va-icon name="text_increase" class="label-icon" />
																</div>
																<div class="card-title-group">
																	<h3 class="card-title">Convertir a mayúsculas</h3>
																	<span style="font-size: smaller">Convierte a mayúsculas los siguientes elementos Nombre Carrera, Nombre asignatura, Campus</span>
																	<span style="font-size: smaller"> Ejem: derecho industrial / DERECHO INDUSTRIAL </span>
																</div>
															</div>
															<div class="card-value" style="margin-top: 4rem">
																<VaRadio
																	v-model="formData.config.certificates.mayus"
																	:options="[
																		{ text: 'Convertir', value: true },
																		{ text: 'Mantener minúsculas', value: false }
																	]"
																	value-by="value" />
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
									<div class="section-title" style="margin-top: 1rem">
										<div class="section-title-content">
											<va-icon name="description" class="title-icon" />
											<span>Configuración de títulos</span>
										</div>
									</div>
									<div class="collapse-body">
										<div class="collapse-content">
											<div class="form-grid">
												<!-- CURP del responsable de firma -->

												<div class="package-grid">
													<div class="package-card">
														<div class="card-gradient transaction-gradient" style="height: 160px"></div>
														<div class="card-content-wrapper">
															<div class="card-header">
																<div class="package-avatar transaction-avatar">
																	<va-icon name="account_balance" class="label-icon" />
																</div>
																<div class="card-title-group">
																	<h3 class="card-title">Conector Carrera-Nivel</h3>
																	<span style="font-size: smaller">Ingrese la preposición que une el nivel con el sustantivo de la carrera</span>
																	<br />
																	<span style="font-size: smaller"> Ejem: Maestría EN Derecho</span>
																</div>
															</div>
															<div class="card-value" style="margin-top: 4.5rem">
																<va-input
																	v-model="formData.config.degree.conectorCarreraNivel"
																	placeholder="Ejem: EN"
																	class="form-input"
																	:rules="[value => !!value || 'El conector carrera-nivel es requerido', value => value.length === 2 || 'El conector carrera-nivel debe tener 2 caracteres']" />
															</div>
														</div>
													</div>
												</div>

												<!-- Nombre de Institución -->
												<div class="package-grid">
													<div class="package-card">
														<div class="card-gradient transaction-gradient" style="height: 160px"></div>
														<div class="card-content-wrapper">
															<div class="card-header">
																<div class="package-avatar transaction-avatar">
																	<va-icon name="check_box" class="label-icon" />
																</div>
																<div class="card-title-group">
																	<h3 class="card-title">Quitar acentos</h3>
																	<span style="font-size: smaller">Normaliza los textos para eliminar acentos del Nombre del alumno y responsable de expedición</span>
																</div>
															</div>
															<div class="card-value" style="margin-top: 6rem">
																<VaRadio
																	v-model="formData.config.degree.hasAccents"
																	:options="[
																		{ text: 'Mantener acentos', value: true },
																		{ text: 'Quitar acentos', value: false }
																	]"
																	value-by="value" />
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
									<div class="section-title" style="margin-top: 1rem">
										<div class="section-title-content">
											<va-icon name="description" class="title-icon" />
											<span>Servicio títulos producción</span>
										</div>
									</div>
									<div class="collapse-body">
										<div class="collapse-content">
											<div class="form-grid">
												<div class="package-grid">
													<div class="package-card">
														<div class="card-gradient transaction-gradient" style="height: 160px"></div>
														<div class="card-content-wrapper">
															<div class="card-header">
																<div class="package-avatar transaction-avatar">
																	<va-icon name="pin" class="label-icon" />
																</div>
																<div class="card-title-group">
																	<h3 class="card-title">Id del servicio de títulos</h3>
																	<span style="font-size: smaller">Identificador del servicio de títulos MET</span>
																</div>
															</div>
															<div class="card-value" style="margin-top: 5.5rem">
																<va-input
																	v-model="formData.config.degree.services.id"
																	placeholder="Ingrese el id del servicio de títulos"
																	class="form-input"
																	:rules="[value => !!value || 'El id del servicio de títulos es requerido']" />
															</div>
														</div>
													</div>
												</div>

												<div class="package-grid">
													<div class="package-card">
														<div class="card-gradient transaction-gradient" style="height: 160px"></div>
														<div class="card-content-wrapper">
															<div class="card-header">
																<div class="package-avatar transaction-avatar">
																	<va-icon name="link" class="label-icon" />
																</div>
																<div class="card-title-group">
																	<h3 class="card-title">Url del servicio de títulos</h3>
																	<span style="font-size: smaller">URL a la cual se va a conectar la institución al servicio de títulos MET</span>
																</div>
															</div>
															<div class="card-value" style="margin-top: 5.5rem">
																<va-input
																	v-model="formData.config.degree.services.url"
																	placeholder="Ingrese la url del servicio de títulos"
																	class="form-input"
																	:rules="[value => !!value || 'La url del servicio de títulos es requerida']" />
															</div>
														</div>
													</div>
												</div>
												<div class="package-grid">
													<div class="package-card">
														<div class="card-gradient transaction-gradient" style="height: 160px"></div>
														<div class="card-content-wrapper">
															<div class="card-header">
																<div class="package-avatar transaction-avatar">
																	<va-icon name="person" class="label-icon" />
																</div>
																<div class="card-title-group">
																	<h3 class="card-title">Usuario del servicio de títulos</h3>
																	<span style="font-size: smaller">Usuario para el acceso al servicio de títulos MET</span>
																</div>
															</div>
															<div class="card-value" style="margin-top: 5.5rem">
																<va-input
																	v-model="formData.config.degree.services.user"
																	placeholder="Ingrese el usuario del servicio de títulos"
																	class="form-input"
																	:rules="[value => !!value || 'El usuario del servicio de títulos es requerido']" />
															</div>
														</div>
													</div>
												</div>
												<div class="package-grid">
													<div class="package-card">
														<div class="card-gradient transaction-gradient" style="height: 160px"></div>
														<div class="card-content-wrapper">
															<div class="card-header">
																<div class="package-avatar transaction-avatar">
																	<va-icon name="key" class="label-icon" />
																</div>
																<div class="card-title-group">
																	<h3 class="card-title">Contraseña del servicio de títulos</h3>
																	<span style="font-size: smaller">Contraseña para el acceso al servicio de títulos MET</span>
																</div>
															</div>
															<div class="card-value" style="margin-top: 5.5rem">
																<va-input
																	v-model="formData.config.degree.services.pass"
																	placeholder="Ingrese la contraseña del servicio de títulos"
																	class="form-input"
																	:rules="[value => !!value || 'El contraseña del servicio de títulos es requerida']" />
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
									<div class="section-title" style="margin-top: 1rem">
										<div class="section-title-content">
											<va-icon name="description" class="title-icon" />
											<span>Servicio títulos pruebas</span>
										</div>
									</div>
									<div class="collapse-body">
										<div class="collapse-content">
											<div class="form-grid">
												<div class="package-grid">
													<div class="package-card">
														<div class="card-gradient-test transaction-gradient-test" style="height: 160px"></div>
														<div class="card-content-wrapper">
															<div class="card-header">
																<div class="package-avatar-test transaction-avatar-test">
																	<va-icon name="pin" class="label-icon-test" />
																</div>
																<div class="card-title-group">
																	<h3 class="card-title">Id del servicio de títulos</h3>
																	<span style="font-size: smaller">Identificador del servicio de títulos MET</span>
																</div>
															</div>
															<div class="card-value" style="margin-top: 5.5rem">
																<va-input
																	v-model="formData.testMode.config.degree.services.id"
																	placeholder="Ingrese el id del servicio de títulos"
																	class="form-input"
																	:rules="[value => !!value || 'El id del servicio de títulos es requerido']" />
															</div>
														</div>
													</div>
												</div>

												<div class="package-grid">
													<div class="package-card">
														<div class="card-gradient-test transaction-gradient-test" style="height: 160px"></div>
														<div class="card-content-wrapper">
															<div class="card-header">
																<div class="package-avatar-test transaction-avatar-test">
																	<va-icon name="link" class="label-icon-test" />
																</div>
																<div class="card-title-group">
																	<h3 class="card-title">Url del servicio de títulos</h3>
																	<span style="font-size: smaller">URL a la cual se va a conectar la institución al servicio de títulos MET</span>
																</div>
															</div>
															<div class="card-value" style="margin-top: 5.5rem">
																<va-input
																	v-model="formData.testMode.config.degree.services.url"
																	placeholder="Ingrese la url del servicio de títulos"
																	class="form-input"
																	:rules="[value => !!value || 'La url del servicio de títulos es requerida']" />
															</div>
														</div>
													</div>
												</div>
												<div class="package-grid">
													<div class="package-card">
														<div class="card-gradient-test transaction-gradient-test" style="height: 160px"></div>
														<div class="card-content-wrapper">
															<div class="card-header">
																<div class="package-avatar-test transaction-avatar-test">
																	<va-icon name="person" class="label-icon-test" />
																</div>
																<div class="card-title-group">
																	<h3 class="card-title">Usuario del servicio de títulos</h3>
																	<span style="font-size: smaller">Usuario para el acceso al servicio de títulos MET</span>
																</div>
															</div>
															<div class="card-value" style="margin-top: 5.5rem">
																<va-input
																	v-model="formData.testMode.config.degree.services.user"
																	placeholder="Ingrese el usuario del servicio de títulos"
																	class="form-input"
																	:rules="[value => !!value || 'El usuario del servicio de títulos es requerido']" />
															</div>
														</div>
													</div>
												</div>
												<div class="package-grid">
													<div class="package-card">
														<div class="card-gradient-test transaction-gradient-test" style="height: 160px"></div>
														<div class="card-content-wrapper">
															<div class="card-header">
																<div class="package-avatar-test transaction-avatar-test">
																	<va-icon name="key" class="label-icon-test" />
																</div>
																<div class="card-title-group">
																	<h3 class="card-title">Contraseña del servicio de títulos</h3>
																	<span style="font-size: smaller">Contraseña para el acceso al servicio de títulos MET</span>
																</div>
															</div>
															<div class="card-value" style="margin-top: 5.5rem">
																<va-input
																	v-model="formData.testMode.config.degree.services.pass"
																	placeholder="Ingrese la contraseña del servicio de títulos"
																	class="form-input"
																	:rules="[value => !!value || 'El contraseña del servicio de títulos es requerida']" />
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</template>
							</VaCollapse>
							<!-- Botones de acción -->
							<div class="form-actions">
								<va-button color="primary" @click="handleSubmit" :loading="submitting" :disabled="submitting" class="action-button">
									<va-icon name="save" class="button-icon" />
									Guardar nueva institución
								</va-button>
							</div>
						</form>
					</VaCardContent>
					<VaCardContent v-if="currentTab?.title === 'Registro de bitácora'">
						<div class="section-title">
							<div class="section-title-row">
								<div class="section-title-heading">
									<div class="section-title-content">
										<va-icon name="description" class="title-icon" />
										<span>Datos de la bitácora a registrar</span>
									</div>
								</div>
								<div class="section-title-actions">
									<VaRadio
										v-model="editarActual"
										:options="[
											{ text: 'Actualizar bitácora de la institución actual', value: true },
											{ text: 'Crear nueva bitácora', value: false }
										]"
										value-by="value" />
								</div>
							</div>
							<p class="form-section-subtitle">Complete la información de la institución y del contrato</p>
						</div>
						<form class="form-container" style="padding: 10px">
							<div class="form-grid form-grid-flat">
								<div class="package-grid">
									<div class="package-card">
										<div class="card-gradient transaction-gradient"></div>
										<div class="card-content-wrapper">
											<div class="card-header">
												<div class="package-avatar transaction-avatar">
													<va-icon name="account_balance" class="label-icon" />
												</div>
												<div class="card-title-group">
													<h3 class="card-title">ID de Institución</h3>
												</div>
											</div>
											<div class="card-value">
												<va-input
													v-model="formData.idInstitution"
													placeholder="Ingrese el ID de la institución"
													class="form-input"
													:rules="[value => !!value || 'El ID de institución es requerido']" />
											</div>
										</div>
									</div>
								</div>

								<div class="package-grid">
									<div class="package-card">
										<div class="card-gradient transaction-gradient"></div>
										<div class="card-content-wrapper">
											<div class="card-header">
												<div class="package-avatar transaction-avatar">
													<va-icon name="business" class="label-icon" />
												</div>
												<div class="card-title-group">
													<h3 class="card-title">Nombre de Institución</h3>
												</div>
											</div>
											<div class="card-value">
												<va-input
													id="bitacora-institution-name"
													v-model="formData.institutionName"
													placeholder="Ingrese el nombre de la institución"
													class="form-input"
													:rules="[value => !!value || 'El nombre de institución es requerido']" />
											</div>
										</div>
									</div>
								</div>

								<div class="package-grid" v-if="formData.plan === EPlan.PREPAID">
									<div class="package-card">
										<div class="card-gradient transaction-gradient"></div>
										<div class="card-content-wrapper">
											<div class="card-header">
												<div class="package-avatar transaction-avatar">
													<va-icon name="science" class="label-icon" />
												</div>
												<div class="card-title-group">
													<h3 class="card-title">Créditos para Producción</h3>
												</div>
											</div>
											<div class="card-value">
												<VaCounter
													class="form-input"
													v-model="formData.totalDocumentsProduction"
													manual-input
													:min="0"
													:step="1"
													:rules="[value => (typeof value === 'number' && value >= 0) || 'El valor no puede ser negativo']" />
											</div>
										</div>
									</div>
								</div>

								<div class="package-grid" v-if="formData.plan === EPlan.PREPAID">
									<div class="package-card">
										<div class="card-gradient transaction-gradient"></div>
										<div class="card-content-wrapper">
											<div class="card-header">
												<div class="package-avatar transaction-avatar">
													<va-icon name="warning" class="label-icon" />
												</div>
												<div class="card-title-group">
													<h3 class="card-title">Créditos para Prueba</h3>
												</div>
											</div>
											<div class="card-value">
												<VaCounter
													class="form-input"
													v-model="formData.totalDocumentsTest"
													manual-input
													:min="0"
													:step="1"
													:rules="[value => (typeof value === 'number' && value >= 0) || 'El valor no puede ser negativo']" />
											</div>
										</div>
									</div>
								</div>

								<div class="package-grid">
									<div class="package-card">
										<div class="card-gradient transaction-gradient"></div>
										<div class="card-content-wrapper">
											<div class="card-header">
												<div class="package-avatar transaction-avatar">
													<va-icon name="event_available" class="label-icon" />
												</div>
												<div class="card-title-group">
													<h3 class="card-title">Fecha de inicio y fin de contrato</h3>
												</div>
											</div>
											<div class="row" style="display: flex; justify-content: space-between">
												<div class="col-md-6">
													<div class="card-value" style="display: flex; flex-direction: column; gap: 0.5rem">
														<VaBadge text="Fecha de inicio" color="primary" class="ml-2" />
														<VueDatePicker id="bitacora-fecha-inicio" text-input v-model="formData.fechaInicioContrato" :formats="{ input: 'dd/MM/yyyy' }" :locale="es" :teleport="true" />
													</div>
												</div>
												<div class="col-md-6">
													<div class="card-value" style="display: flex; flex-direction: column; gap: 0.5rem">
														<VaBadge text="Fecha de fin" color="primary" class="ml-2" />
														<VueDatePicker
															id="bitacora-fecha-fin"
															text-input
															v-model="formData.fechaFinContrato"
															:formats="{ input: 'dd/MM/yyyy' }"
															:locale="es"
															:teleport="true"
															:min-date="minDateFinContrato" />
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>

								<div class="package-grid" v-if="formData.plan !== EPlan.UNLIMITED && formData.plan !== EPlan.BASIC">
									<div class="package-card">
										<div class="card-gradient transaction-gradient"></div>
										<div class="card-content-wrapper">
											<div class="card-header">
												<div class="package-avatar transaction-avatar">
													<va-icon name="attach_money" class="label-icon" />
												</div>
												<div class="card-title-group">
													<h3 class="card-title">Precio por Documento</h3>
												</div>
											</div>
											<div class="card-value">
												<VaCounter
													class="form-input"
													v-model="formData.pricePerDocument"
													manual-input
													:min="0"
													:step="1"
													:rules="[value => (typeof value === 'number' && value >= 0) || 'El precio no puede ser negativo']" />
											</div>
										</div>
									</div>
								</div>
								<div class="package-grid">
									<div class="package-card">
										<div class="card-gradient transaction-gradient"></div>
										<div class="card-content-wrapper">
											<div class="card-header">
												<div class="package-avatar transaction-avatar">
													<va-icon name="attach_money" class="label-icon" />
												</div>
												<div class="card-title-group">
													<div class="row">
														<h3 class="card-title col-md-4">Precio del plan</h3>
														<VaButton
															v-if="formData.plan === EPlan.POSTPAID"
															icon="warning"
															color="warning"
															size="small"
															class="col-md-4"
															@click="calcularPrecioPlan(JSON.parse(JSON.stringify(mesSeleccionado)))">
															<span>Calcular</span>
														</VaButton>
														<VueDatePicker
															v-if="formData.plan === EPlan.POSTPAID"
															class="col-md-4"
															v-model="mesSeleccionado"
															:formats="{ input: 'MM/yyyy' }"
															:locale="es"
															:teleport="true"
															month-picker
															auto-apply />
													</div>
												</div>
											</div>
											<div class="card-value">
												<VaInput
													v-model="formData.precioPlan"
													class="form-input"
													input-class="va-text-center"
													type="number"
													:rules="[value => (typeof value === 'number' && value >= 0) || 'El precio del plan no puede ser negativo']" />
											</div>
										</div>
									</div>
								</div>
								<div class="package-grid">
									<div class="package-card">
										<div class="card-gradient transaction-gradient"></div>
										<div class="card-content-wrapper">
											<div class="card-header">
												<div class="package-avatar transaction-avatar">
													<va-icon name="attach_money" class="label-icon" />
												</div>
												<div class="card-title-group">
													<h3 class="card-title">IVA</h3>
												</div>
											</div>
											<div class="card-value">
												<VaInput
													v-model="formData.iva"
													class="form-input"
													input-class="va-text-center"
													type="number"
													:rules="[value => (typeof value === 'number' && value >= 0) || 'El IVA no puede ser negativo']" />
											</div>
										</div>
									</div>
								</div>
								<div class="package-grid" v-if="formData.plan === EPlan.POSTPAID">
									<div class="package-card">
										<div class="card-gradient transaction-gradient"></div>
										<div class="card-content-wrapper">
											<div class="card-header">
												<div class="package-avatar transaction-avatar">
													<va-icon name="attach_money" class="label-icon" />
												</div>
												<div class="card-title-group">
													<h3 class="card-title">Límite de documentos para postpago</h3>
												</div>
											</div>
											<div class="card-value">
												<VaCounter
													class="form-input"
													v-model="formData.limitDocumentsPostpaid"
													manual-input
													:min="0"
													:step="1"
													:rules="[value => (typeof value === 'number' && value >= 0) || 'El límite de documentos para postpago no puede ser negativo']" />
											</div>
										</div>
									</div>
								</div>
								<div class="package-grid checkbox-group">
									<div class="package-card">
										<div class="card-gradient transaction-gradient"></div>
										<div class="card-content-wrapper">
											<div class="card-header">
												<div class="package-avatar transaction-avatar">
													<va-icon name="check_circle" class="label-icon" />
												</div>
												<div class="card-title-group">
													<h3 class="card-title">Tipo de plan</h3>
												</div>
											</div>
											<div class="card-value">
												<VaRadio v-model="formData.plan" :options="PLAN_OPTIONS" value-by="value" />
											</div>
										</div>
									</div>
								</div>
							</div>

							<div class="form-actions">
								<va-button color="secondary" @click="handleReset" :disabled="submitting" class="action-button">
									<va-icon name="refresh" class="button-icon" />
									Restablecer
								</va-button>
								<va-button color="primary" @click="handleSubmitBitacora" :loading="submitting" :disabled="submitting" class="action-button">
									<va-icon name="save" class="button-icon" />
									Guardar
								</va-button>
							</div>
						</form>
					</VaCardContent>
				</VaCard>
			</VaTabs>

			<!-- Sección de Formulario Registro -->

			<!-- Sección de Formulario Actualización -->
		</va-card-content>
	</va-card>
	<BaseModal v-model="showErrorModal" title="Error" :message="error || 'Error al guardar los datos'" :test-mode="true" size="medium">
		<template #body>
			<div class="error-message">
				<div class="error-header">
					<div class="error-icon">
						<i class="fas fa-exclamation-triangle"></i>
					</div>
					<h3 class="error-title">Error</h3>
				</div>
				<p class="error-description">{{ error || 'Error al guardar los datos' }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
		</template>
	</BaseModal>
	<BaseModal v-model="showSuccessModal" title="Éxito" :message="successMessage || 'Datos guardados correctamente'" :test-mode="false" size="medium">
		<template #body>
			<div class="success-message">
				<div class="success-header">
					<div class="success-icon">
						<i class="fas fa-check-circle"></i>
					</div>
					<h3 class="success-title">Datos guardados correctamente</h3>
				</div>
				<p class="success-description">{{ successMessage || 'Datos guardados correctamente' }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showSuccessModal = false">Entendido</button>
		</template>
	</BaseModal>
	<ResponsablesModal v-model:isMenuOpen="showModalUploadCertificate" v-model:responsables="formData.Responsable" @update:responsables="onUpdateResponsable" />
</template>

<style scoped>
.error-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #f71d1d;
}
.error-message {
	background: linear-gradient(135deg, #ecbaba 0%, #e67a7a 100%);
	border: 2px solid #f71d1d;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInError 0.4s ease-out;
}

.error-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #f68484;
}

.error-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #e67a7a 0%, #e67a7a 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.error-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.error-title {
	margin: 0;
	color: #e67a7a;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.success-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #28a745;
}
.success-message {
	background: linear-gradient(135deg, #28a745 0%, #28a745 100%);
	border: 2px solid #28a745;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInSuccess 0.4s ease-out;
}

.success-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #ffffff;
}

.success-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #28a745 0%, #28a745 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.success-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.success-title {
	margin: 0;
	color: #ffffff;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.package-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
	gap: 1.5rem;
}

/* Tarjetas base */
.package-card {
	position: relative;
	background: white;
	border-radius: 16px;
	padding: 0;
	transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
	overflow: hidden;
	animation: fadeInScale 0.6s ease-out;
	border: 1px solid rgba(0, 0, 0, 0.05);
}

.package-card::before {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	height: 4px;
	background: linear-gradient(90deg, transparent 0%, rgba(102, 126, 234, 0.5) 50%, transparent 100%);
	opacity: 0;
	transition: opacity 0.3s ease;
}

/* Gradientes de tarjetas */
.card-gradient {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	height: 120px;
	border-radius: 16px 16px 0 0;
	opacity: 0.15;
	transition: opacity 0.3s ease;
}

.transaction-gradient {
	background: linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%);
}
.package-card:hover .card-gradient {
	opacity: 0.25;
}
/* Gradientes de tarjetas */
.card-gradient-test {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	height: 120px;
	border-radius: 16px 16px 0 0;
	opacity: 0.15;
	transition: opacity 0.3s ease;
}

.transaction-gradient-test {
	background: linear-gradient(135deg, #eb2525 0%, #8a1e1e 100%);
}
.package-card:hover .card-gradient-test {
	opacity: 0.25;
}
.card-content-wrapper {
	position: relative;
	padding: 1.75rem;
	z-index: 1;
}

.card-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
}

.card-title-group {
	flex: 1;
}

.card-title {
	display: flex;
	align-items: center;
	gap: 1rem;
	font-size: 14px;
	font-weight: 700;
	color: #1a2a3a;
	padding: 20px 20px 0 20px;
}

/* Avatares */
.package-avatar {
	width: 56px;
	height: 56px;
	border-radius: 14px;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
	transition: all 0.3s ease;
	position: relative;
	overflow: hidden;
}

.package-avatar::before {
	content: '';
	position: absolute;
	top: -50%;
	left: -50%;
	width: 200%;
	height: 200%;
	background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.3), transparent);
	transform: rotate(45deg);
	transition: all 0.5s ease;
}

.package-card:hover .package-avatar::before {
	animation: shine 0.6s ease;
}

.transaction-avatar {
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
}
/* Avatares test*/
.package-avatar-test {
	width: 56px;
	height: 56px;
	border-radius: 14px;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
	transition: all 0.3s ease;
	position: relative;
	overflow: hidden;
}

.package-avatar-test::before {
	content: '';
	position: absolute;
	top: -50%;
	left: -50%;
	width: 200%;
	height: 200%;
	background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.3), transparent);
	transform: rotate(45deg);
	transition: all 0.5s ease;
}

.transaction-avatar-test {
	background: linear-gradient(135deg, #eb2525 0%, #8a1e1e 100%);
}
.card-value {
	font-size: 16px;
	font-weight: 500;
	margin-top: 4rem;
	transition: all 0.3s ease;
}

.bitacora-card {
	border-radius: 20px;
	padding: 1rem;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
	animation: fadeInUp 0.6s ease-out;
	overflow: hidden;
	background: white;
	border: none;
	width: 100%;
	display: flex;
	flex-direction: column;
}

/* Header mejorado */
.header-gradient {
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	padding: 0.5rem;
	border-radius: 0;
	position: relative;
	flex-shrink: 0;
}

.header-gradient::before {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%);
	pointer-events: none;
}

.header-content {
	position: relative;
	z-index: 1;
}

.header-main {
	display: flex;
	align-items: center;
	gap: 1.5rem;
}

.header-icon-wrapper {
	width: 64px;
	height: 64px;
	border-radius: 16px;
	background: rgba(255, 255, 255, 0.2);
	backdrop-filter: blur(10px);
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
	transition: all 0.3s ease;
}

.header-icon-wrapper:hover {
	transform: scale(1.1) rotate(5deg);
	background: rgba(255, 255, 255, 0.3);
}

.header-icon {
	color: white;
	font-size: 2rem;
}

.header-text {
	flex: 1;
}

.header-title {
	font-size: 1.75rem;
	font-weight: 700;
	color: white;
	margin: 0;
	text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
	letter-spacing: -0.5px;
}

.card-content {
	padding: 2.5rem;
	padding-bottom: 3rem;
	background: #fafbfc;
	overflow-y: auto;
	max-height: calc(100vh - 200px);
}

/* Scrollbar personalizado para el contenido */
.card-content::-webkit-scrollbar {
	width: 8px;
}

.card-content::-webkit-scrollbar-track {
	background: #f1f1f1;
	border-radius: 4px;
}

.card-content::-webkit-scrollbar-thumb {
	background: #888;
	border-radius: 4px;
}

.card-content::-webkit-scrollbar-thumb:hover {
	background: #555;
}

/* Secciones */
.section-title {
	margin-bottom: 1.25rem;
	padding-bottom: 1rem;
	border-bottom: 3px solid transparent;
	border-image: linear-gradient(135deg, #062342 0%, #062342 100%) 1;
	animation: slideInLeft 0.5s ease-out;
	min-width: 0;
}

.section-title-row {
	display: flex;
	flex-wrap: wrap;
	gap: 1rem;
	align-items: center;
	min-width: 0;
}

.section-title-heading {
	flex: 1 1 200px;
	min-width: 0;
}

.section-title-actions {
	flex: 0 1 auto;
	min-width: 0;
	max-width: 100%;
}

.section-title-actions :deep(.va-radio-group) {
	display: flex;
	flex-wrap: wrap;
	gap: 0.75rem 1.25rem;
}

.section-title-actions :deep(.va-radio-option),
.section-title-actions :deep(.va-radio) {
	flex-shrink: 0;
	min-width: 0;
}

.section-title-actions :deep(label) {
	white-space: normal;
	word-wrap: break-word;
	max-width: 100%;
}

.section-title-content {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	font-size: 1.5rem;
	font-weight: 700;
	color: #1a202c;
	min-width: 0;
}

.section-title-content span {
	min-width: 0;
	word-wrap: break-word;
}

.form-section-subtitle {
	margin: 0.5rem 0 0;
	font-size: 0.9375rem;
	font-weight: 400;
	color: #4a5568;
	line-height: 1.4;
}

.title-icon {
	background: #062342;
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	background-clip: text;
	color: #363842;
	font-size: 1.75rem;
	animation: pulse 2s ease-in-out infinite;
}

.upload-section {
	background: white;
	border-radius: 12px;
	padding: 1.5rem;
	margin-top: 1.5rem;
	margin-bottom: 1.5rem;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
	border: 1px solid rgba(0, 0, 0, 0.05);
	transition: all 0.3s ease;
}

.upload-section:hover {
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
	transform: translateY(-2px);
}

.upload-section-header {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	margin-bottom: 1rem;
	padding-bottom: 0.75rem;
	border-bottom: 2px solid #f0f0f0;
}

.upload-section-icon {
	color: #062342;
	font-size: 1rem;
	background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(102, 126, 234, 0.2) 100%);
	border-radius: 8px;
}

.upload-section-title {
	font-size: 1.125rem;
	font-weight: 600;
	color: #1a202c;
	margin: 0;
	letter-spacing: -0.3px;
}

.upload-section-content {
	padding: 1rem;
	display: flex;
	flex-direction: column;
	gap: 1rem;
	align-items: center;
	justify-content: center;
}

.upload-badge {
	margin-bottom: 0.5rem;
	align-self: flex-start;
}
.upload-button-container {
	flex: 0 0 260px;
	display: flex;
	align-items: flex-start;
}

.upload-button {
	width: 100%;
	height: 50px;
	border-radius: 12px;
}

.responsables-layout {
	display: flex;
	gap: 1rem;
	align-items: stretch;
	margin: 1rem 0;
}

.responsables-content {
	flex: 1;
	min-width: 0;
}

.responsables-table {
	background: rgba(255, 255, 255, 0.8);
	border: 1px solid rgba(0, 0, 0, 0.06);
	border-radius: 14px;
	padding: 0.5rem;
	box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
}

.responsables-empty {
	background: rgba(255, 255, 255, 0.6);
	border: 1px dashed rgba(0, 0, 0, 0.12);
	border-radius: 14px;
	padding: 1rem;
	margin: 0;
}

@media (max-width: 900px) {
	.responsables-layout {
		flex-direction: column;
	}
	.upload-button-container {
		flex: 0 0 auto;
	}
}
/* Estilos para el modal de subida de certificado digital */
.modal-card {
	background: rgba(255, 255, 255, 0.95);
	backdrop-filter: blur(10px);
	border-radius: 20px;
	border: 1px solid rgba(255, 255, 255, 0.2);
}

.upload-container {
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.file-input-section {
	margin-bottom: 15px;
}

.file-info {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 10px;
	background: #f8f9fa;
	border-radius: 12px;
	margin-top: 10px;
}

.file-icon {
	color: #667eea;
	font-size: 20px;
}

.conversion-status {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 15px;
	padding: 30px;
	background: #f8f9fa;
	border-radius: 16px;
	border: 2px dashed #667eea;
}

.conversion-status p {
	margin: 0;
	color: #6c757d;
	font-weight: 600;
	font-size: 16px;
}

.modal-actions {
	display: flex;
	justify-content: flex-end;
	gap: 10px;
	padding: 15px 0;
}

.va-input__error {
	color: #dc3545;
	font-size: 14px;
	margin-top: 5px;
	font-weight: 600;
}

/* Estilos compartidos para el certificado dentro del modal */
.certificate-card {
	background: rgba(255, 255, 255, 0.95);
	backdrop-filter: blur(10px);
	border-radius: 20px;
	border: 1px solid rgba(255, 255, 255, 0.2);
	transition:
		transform 0.3s ease,
		box-shadow 0.3s ease;
}

.certificate-info {
	display: flex;
	flex-direction: column;
	gap: 15px;
	padding: 15px 0;
}

.certificate-item {
	display: flex;
	align-items: center;
	gap: 15px;
	padding: 15px;
	background: #f8f9fa;
	border-radius: 16px;
	border-left: 5px solid #28a745;
	transition: all 0.3s ease;
}

.certificate-item:hover {
	transform: translateX(5px);
	box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.certificate-icon {
	color: #4aec3c;
	font-size: 15px;
}

.certificate-content {
	flex: 1;
}

.radio-vertical-wrapper {
	display: block;
	width: 100%;
}

/* Forzar layout vertical para VaRadio con options */
.radio-vertical-wrapper :deep(> *) {
	display: flex !important;
	flex-direction: column !important;
	flex-wrap: nowrap !important;
	gap: 0.75rem !important;
	width: 100% !important;
}

.radio-vertical-wrapper :deep(.va-radio-group) {
	display: flex !important;
	flex-direction: column !important;
	flex-wrap: nowrap !important;
	gap: 0.75rem !important;
	width: 100% !important;
}

.radio-vertical-wrapper :deep(.va-radio) {
	display: flex !important;
	flex-direction: row !important;
	align-items: center !important;
	width: 100% !important;
	margin-right: 0 !important;
	margin-bottom: 0 !important;
}

/* Asegurar que cualquier contenedor interno también sea vertical */
.radio-vertical-wrapper :deep(div[class*='va-radio']) {
	display: flex !important;
	flex-direction: column !important;
	gap: 0.75rem !important;
}

.certificate-label {
	display: block;
	font-size: 14px;
	font-weight: 600;
	color: #6c757d;
	margin-bottom: 5px;
	text-transform: uppercase;
	letter-spacing: 0.5px;
}

.certificate-value {
	display: block;
	font-size: 16px;
	font-weight: 700;
	color: #1a2a3a;
	word-break: break-all;
}

/* Responsive design para el modal */
@media (max-width: 768px) {
	.modal-actions {
		flex-direction: column;
	}

	.file-info {
		flex-direction: column;
		text-align: center;
	}

	.conversion-status {
		padding: 20px;
	}

	.card-title {
		padding: 15px 15px 0 15px;
		font-size: 12px;
	}

	.certificate-item {
		padding: 10px;
		gap: 10px;
	}

	.certificate-value {
		font-size: 14px;
	}
}

/* Animaciones */
@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes fadeInScale {
	from {
		opacity: 0;
		transform: scale(0.9);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

@keyframes slideInLeft {
	from {
		opacity: 0;
		transform: translateX(-20px);
	}
	to {
		opacity: 1;
		transform: translateX(0);
	}
}

@keyframes pulse {
	0%,
	100% {
		transform: scale(1);
	}
	50% {
		transform: scale(1.1);
	}
}

@keyframes shine {
	0% {
		transform: translateX(-100%) translateY(-100%) rotate(45deg);
	}
	100% {
		transform: translateX(100%) translateY(100%) rotate(45deg);
	}
}

@keyframes float {
	0%,
	100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-10px);
	}
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

@media (max-width: 768px) {
	.card-content {
		padding: 1.5rem;
		padding-bottom: 2rem;
	}

	.header-gradient {
		padding: 1.5rem;
	}

	.header-main {
		flex-direction: column;
		align-items: flex-start;
		gap: 1rem;
	}

	.header-icon-wrapper {
		width: 56px;
		height: 56px;
	}

	.header-icon {
		font-size: 1.75rem;
	}

	.header-title {
		font-size: 1.5rem;
	}

	.section-title-content {
		font-size: 1.25rem;
	}

	.section-title-heading {
		flex-basis: 100%;
	}

	.section-title-actions {
		flex-basis: 100%;
	}
}

@media (max-width: 480px) {
	.card-content {
		padding: 1.25rem;
		padding-bottom: 1.75rem;
	}

	.header-gradient {
		padding: 1.25rem;
	}
}

/* Estilos del formulario */
.alert-message {
	margin-bottom: 1.5rem;
	animation: fadeInUp 0.3s ease-out;
}

.form-container {
	width: 100%;
	overflow: hidden;
}

.form-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
	gap: 1.5rem;
	margin-bottom: 2rem;
}

.form-grid-flat {
	grid-template-columns: repeat(2, 1fr);
	gap: 1.25rem 2rem;
}

.form-field {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.form-field-label {
	font-size: 0.875rem;
	font-weight: 600;
	color: #1a202c;
	line-height: 1.4;
}

.form-field-full {
	grid-column: 1 / -1;
}

.form-field-radios-wrapper {
	margin: 0;
	padding: 0;
	border: none;
}

.form-field-radios-wrapper .form-field-label {
	margin-bottom: 0.5rem;
	display: block;
}

.form-field-radios {
	display: flex;
	flex-wrap: wrap;
	gap: 1rem 1.5rem;
	align-items: center;
}

.checkbox-group {
	grid-column: 1 / -1;
	padding: 1rem;
	background: #f8f9fa;
	border-radius: 12px;
	border: 1px solid #e9ecef;
}

.label-icon {
	color: #fcfcfc;
	font-size: 1.125rem;
}
.label-icon-test {
	color: #f7f7f7;
	font-size: 1.125rem;
}

.form-input {
	width: 100%;
}

.form-actions {
	display: flex;
	justify-content: flex-end;
	gap: 1rem;
	padding-top: 1.5rem;
	border-top: 2px solid #e9ecef;
	margin-top: 1.5rem;
}

.action-button {
	min-width: 140px;
}

.button-icon {
	margin-right: 0.5rem;
}

/* Asegurar que la card dentro de VaTabs ocupe todo el ancho */
:deep(.va-tabs) {
	width: 100%;
}

:deep(.va-tabs__content) {
	width: 100%;
	display: block;
}

:deep(.va-tabs__content > .va-card) {
	width: 100% !important;
	max-width: 100% !important;
	margin-left: 0 !important;
	margin-right: 0 !important;
}

/* Responsive del formulario */
@media (max-width: 768px) {
	.form-grid,
	.form-grid-flat {
		grid-template-columns: 1fr;
		gap: 1.25rem;
	}

	.form-actions {
		flex-direction: column;
	}

	.action-button {
		width: 100%;
	}
}

/* Estilos del collapse */
.collapse-container {
	margin: 1.5rem 0;
	border-radius: 12px;
	overflow: hidden;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.collapse-header {
	width: 100%;
	display: flex;
	align-items: center;
	padding: 1rem 1.25rem;
	background: var(--va-background-element);
	border: 2px solid var(--va-background-border);
	border-radius: 12px;
	cursor: pointer;
	transition: all 0.3s ease;
}

.collapse-header:hover {
	background: var(--va-background-element-hover, rgba(102, 126, 234, 0.05));
	border-color: var(--va-primary, #667eea);
}

.collapse-icon {
	color: var(--va-primary, #667eea);
	font-size: 1.25rem;
	transition: transform 0.3s ease;
	margin-right: 0.75rem;
}

.collapse-icon.rotate-180 {
	transform: rotate(180deg);
}

.collapse-title {
	font-size: 1rem;
	font-weight: 600;
	color: var(--va-text-primary, #1a202c);
	flex: 1;
}

.collapse-body {
	border: 2px solid var(--va-background-border);
	border-top: none;
	border-radius: 0 0 12px 12px;
	background: var(--va-background-element);
}

.collapse-content {
	padding: 1.25rem;
	color: var(--va-text-secondary, #4a5568);
	line-height: 1.6;
}
</style>
